/**
 * Out-of-band trust anchor(s) for the `@abax/ui` Module Federation manifest.
 *
 * These are the ES256 (EC P-256) PUBLIC keys that verify the detached JWS
 * signature over `mf-manifest.json` (see
 * `src/utils/module-federation/mf2-security-plugin.ts`). They are baked into the
 * host bundle ON PURPOSE:
 *
 *   The manifest and its `.jws` sidecar are served from the same CDN that a
 *   compromise would control. If the verifying key were also fetched from that
 *   CDN, an attacker who rewrites the manifest could rewrite the key that checks
 *   it — circular trust, no protection. Baking the public key into the shell
 *   build keeps it out of the @abax/ui folder it verifies. Only a signature
 *   produced by the matching PRIVATE key (held in a CI File-type variable, never
 *   committed) verifies against it.
 *
 *   Where the anchor stops: when the shell's own bundles are served from the CDN
 *   as well (SHELL_ASSET_BASE), they carry no integrity hash, so a writer of the
 *   whole bucket can replace the bundle that holds this key. The anchor then
 *   holds against a rewrite of the @abax/ui folder, not of the bucket.
 *
 * A public key is PUBLIC data — committing it (even a placeholder) leaks
 * nothing. The private keys must NEVER be committed.
 *
 * ENVIRONMENT-SCOPED KEYS (the forward-deploy invariant — see the AFF MF2
 * migration reference §3.6). Staging and production sign with SEPARATE keys so a
 * feature-branch staging deploy can sign WITHOUT the protected production key,
 * which is what keeps the signed `mf-manifest.json` pointer and the legacy
 * `/latest/` mirror advancing together (a split takes abax-ui down for every
 * consumer):
 *   - Production signs with `MF_MANIFEST_SIGNING_KEY` (PROTECTED CI var, tags).
 *   - Staging signs with `MF_MANIFEST_SIGNING_KEY_STAGING` (UNPROTECTED CI var,
 *     readable on every ref, so every staging deploy signs).
 * `getTrustedAbaxUiManifestPublicKeys(environment)` selects which key(s) the host
 * trusts at runtime (from env-config `ENVIRONMENT`): production trusts ONLY the
 * production key; every other environment trusts staging AND production (so a
 * staging deploy signed by either verifies, while a staging signature never
 * verifies on a production host). One production-built image still serves every
 * environment.
 *
 * ROTATION / PROVISIONING (per key): generate a new EC P-256 keypair, update the
 * matching CI signing variable (File type) with the new PKCS8 private key,
 * replace the SPKI PEM below with the matching public key, and DELETE that key's
 * placeholder sentinel line. Rotate producer and host together for that
 * environment — a manifest signed by one key never verifies against another.
 *
 *   # derive the SPKI public PEM from the PKCS8 private key:
 *   openssl pkey -in mf-manifest-signing.key -pubout
 *
 * Guards keep an unreplaced PRODUCTION key off a protected ref: a grep for the
 * production sentinel below (gitlab-ci/pipelines/shell.yml `build`, kaniko image) and a
 * per-variant key-derivation match in sign-ui
 * (packages/ui/scripts/check-public-key.mjs).
 *
 * If a trusted key is wrong, stale, or a placeholder, `importSPKI` for THAT key
 * is skipped and the remaining trusted key(s) are tried; if none verifies the
 * remote fails CLOSED (abax-ui degrades, `__ABAX_UI_LOAD_FAILED__` is set). No
 * trusted key, no load.
 */

/** Production trust root. Signed by the PROTECTED `MF_MANIFEST_SIGNING_KEY`. */
export const ABAX_UI_MANIFEST_PUBLIC_KEY_PRODUCTION = `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEm1m8Supy7oFHrZuYxpYy6z3heD96
280YAbGaB2H+/5YIr6EFhaDw6rhyHd7fd/MAqHkFotttzM5u1oQr9d0aBQ==
-----END PUBLIC KEY-----
`;

/**
 * Staging trust root. Signed by the UNPROTECTED `MF_MANIFEST_SIGNING_KEY_STAGING`
 * CI variable (readable on every ref, so a feature-branch staging deploy signs
 * and advances both the signed pointer and the /latest/ mirror together). Rotate
 * per the ROTATION note above — regenerate the keypair, update the CI variable,
 * and replace the PEM below.
 */
export const ABAX_UI_MANIFEST_PUBLIC_KEY_STAGING = `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEpJEaxZIcOB+TgzVnugmbnZUpqtXq
kwDywae9fzKtKW/2vByHBEq/e1JtkZwfQ6TSGPa1JSZf5ZD3oBo3s9CiDw==
-----END PUBLIC KEY-----
`;

/**
 * Back-compat single-key export (the production trust root). Existing importers
 * of `ABAX_UI_MANIFEST_PUBLIC_KEY` keep verifying against the production key.
 */
export const ABAX_UI_MANIFEST_PUBLIC_KEY = ABAX_UI_MANIFEST_PUBLIC_KEY_PRODUCTION;

/**
 * Whether `environment` trusts the staging key. DENY BY DEFAULT: `production`
 * and ANY unrecognized value (missing or misconfigured `ENVIRONMENT`, a typo,
 * wrong case, or whitespace drift) do not. The value is normalized (trimmed,
 * lower-cased) before matching, so only a genuinely non-production environment
 * widens the trust set.
 */
export function trustsStagingAbaxUiManifestKey(environment: string | undefined): boolean {
  const env = (environment ?? '').trim().toLowerCase();
  return env === 'staging' || env === 'development' || env === 'local';
}

/**
 * Trusted verification key(s) for a runtime environment. Explicit NON-production
 * environments (`staging`, `development`, `local`) trust the staging key AND the
 * production key, so a staging-signed deploy verifies there (and master->staging,
 * production-signed, verifies via the production key). Everything else trusts
 * ONLY the production key: a drifted production pod must never fall through to
 * accepting the unprotected staging key.
 */
export function getTrustedAbaxUiManifestPublicKeys(environment: string | undefined): string[] {
  return trustsStagingAbaxUiManifestKey(environment)
    ? [ABAX_UI_MANIFEST_PUBLIC_KEY_STAGING, ABAX_UI_MANIFEST_PUBLIC_KEY_PRODUCTION]
    : [ABAX_UI_MANIFEST_PUBLIC_KEY_PRODUCTION];
}
